import { Show } from "solid-js";
import type { GameController } from "../../app/controller";
import { PieceIcon } from "../components/PieceIcon";
import { Icon } from "../components/Icon";

export function GameOverScreen(props: {
  controller: GameController;
  onExit: () => void;
  /** Optional "back to home" action, distinct from onExit (e.g. New match). */
  onHome?: () => void;
  online?: boolean;
  vsAi?: boolean;
  ratingChange?: () => { readonly delta: number; readonly rating: number } | null;
}) {
  const c = props.controller;
  const winner = () => c.store.state.winner;
  const isDraw = () => c.store.state.phase === "finished" && winner() === null;
  const winnerName = () => {
    const w = winner();
    return w === null ? "" : c.store.state.players[w].name;
  };
  // Both online and single-player know which seat is "you".
  const perspective = () => props.online || props.vsAi;
  const youWon = () => perspective() && winner() === c.mySeat;
  // How did the game end? Distinguish a board win from a clock loss / resign.
  const lastKind = () => c.store.state.log[c.store.state.log.length - 1]?.kind;
  const byTimeout = () => lastKind() === "timeout";
  const byResign = () => lastKind() === "resign";
  const byAgreement = () => lastKind() === "drawAgreed";
  const byTurnLimit = () => lastKind() === "turnLimit";

  const heading = () => {
    if (isDraw()) return "Draw";
    if (perspective()) return youWon() ? "You win" : `${winnerName()} wins`;
    return `${winnerName()} wins`;
  };
  const endGlyph = () => {
    if (isDraw()) return <Icon name="handshake" />;
    if (byResign()) return <Icon name="flag" />;
    if (byTimeout()) return <Icon name="clock" />;
    return <PieceIcon type="king" owner={winner() ?? 0} />;
  };
  const reason = () => {
    if (byAgreement()) return "Both players agreed to a draw. Good game.";
    if (byTurnLimit())
      return `The turn limit was reached — the game is a draw. Good game.`;
    if (isDraw()) return "Stalemate, or no progress for a long while — the game is a draw. Good game.";
    if (byResign()) {
      if (perspective()) return youWon() ? "Your opponent resigned. Good game." : "You resigned. Good game.";
      return `${c.store.state.players[winner() === 0 ? 1 : 0].name} resigned. Good game.`;
    }
    if (byTimeout()) {
      if (perspective()) {
        return youWon() ? "Their clock ran out. Good game." : "Your clock ran out. Good game.";
      }
      return `${c.store.state.players[winner() === 0 ? 1 : 0].name}'s clock ran out. Good game.`;
    }
    return "Checkmate — the enemy King has no escape. Good game.";
  };

  return (
    <Show when={c.store.state.phase === "finished"}>
      <div class="overlay">
        <div class="overlay-card">
          <div class="big-glyph">{endGlyph()}</div>
          <h2>{heading()}</h2>
          <p>{reason()}</p>
          <Show when={props.ratingChange?.()}>
            {(rc) => (
              <div class="rating-change" classList={{ up: rc().delta >= 0, down: rc().delta < 0 }}>
                {rc().delta >= 0 ? "+" : ""}
                {rc().delta} rating · now {rc().rating}
              </div>
            )}
          </Show>
          <div class="field-row" style={{ "justify-content": "center", gap: "8px" }}>
            <Show when={!props.online}>
              <button class="btn" onClick={() => c.undo()}>
                Undo last move
              </button>
            </Show>
            <Show when={props.online}>
              <a class="btn" href={`/r/${c.store.state.id}`}>
                Watch replay
              </a>
            </Show>
            <Show when={props.onHome}>
              <button class="btn" onClick={() => props.onHome!()}>
                Home
              </button>
            </Show>
            <button class="btn btn-primary" onClick={props.onExit}>
              {props.online ? "Leave" : "New match"}
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
